import React from 'react';
import PropTypes from 'prop-types';

// Whether `target` sits inside a control that would submit `form` natively:
// a <button> (whose type defaults to "submit") or an <input type="submit">
// or <input type="image">, and one that actually belongs to this form.
const isSubmitterOf = (target, form) => {
  let node = target;
  while (node && node !== form) {
    const tag = node.tagName;
    if (tag === 'BUTTON' || tag === 'INPUT') {
      return node.form === form && (node.type === 'submit' || node.type === 'image');
    }
    node = node.parentNode;
  }
  return false;
};

const createSubmitEvent = () => {
  if (typeof Event === 'function') {
    return new Event('submit', { bubbles: true, cancelable: true });
  }
  // IE11 has no Event constructor.
  const event = document.createEvent('Event');
  event.initEvent('submit', true, true);
  return event;
};

const Form = props => {
  const { children, contentRef, onClick, ...restProps } = props;

  // In a sandboxed tab without allow-forms the browser drops a native form
  // submission before it fires the submit event, so a React onSubmit never
  // runs and the submit button silently does nothing. Such tabs come from
  // links opened out of sandboxed iframes (e.g. an email shown in a webmail
  // client) and keep the sandbox for their whole life.
  //
  // So a click on a submit button does not go through the browser's
  // submission algorithm at all: cancel the button's native activation and
  // dispatch the submit event ourselves. A script-dispatched submit event is
  // never blocked, and onSubmit handlers still receive the same cancelable
  // event with the form as target that they always did.
  const handleClick = e => {
    if (onClick) {
      onClick(e);
    }
    if (e.isDefaultPrevented() || !isSubmitterOf(e.target, e.currentTarget)) {
      return;
    }
    e.preventDefault();
    e.currentTarget.dispatchEvent(createSubmitEvent());
  };

  const formProps = {
    // These are mainly default values for the server
    // rendering. Otherwise the form would submit potentially
    // sensitive data with the default GET method until the client
    // side code is loaded.
    method: 'post',
    action: '/',

    // allow content ref function to be passed to the form
    ref: contentRef,

    onClick: handleClick,

    ...restProps,
  };
  return <form {...formProps}>{children}</form>;
};

Form.defaultProps = {
  children: null,
  contentRef: null,
  onClick: null,
};

const { func, node } = PropTypes;

Form.propTypes = {
  children: node,
  contentRef: func,
  onClick: func,
};

export default Form;
